import React, { useCallback, useRef } from "react";
import { MentionsInput } from "react-mentions";
import {
  getMentionsInputStyles,
  MENTIONS_SUGGESTIONS_PORTAL_HOST,
} from "src/components/general/mentionInputStyles";

/**
 * MentionsInput with suggestions portaled to document.body so the @ dropdown
 * is not clipped by reconcile sidebars, modals, or overflow:auto parents.
 */
export default function CommentMentionsInput({
  className = "comment-input",
  style,
  allowSuggestionsAboveCursor = true,
  singleLine = false,
  inputRef,
  onChange,
  onKeyDown,
  rows = 2,
  value,
  ...props
}) {
  const localInputRef = useRef(null);

  const assignInputRef = useCallback(
    (el) => {
      localInputRef.current = el;
      if (typeof inputRef === "function") {
        inputRef(el);
      } else if (inputRef && typeof inputRef === "object") {
        inputRef.current = el;
      }
    },
    [inputRef]
  );

  const handleChange = useCallback(
    (...args) => {
      onChange?.(...args);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (
        !singleLine &&
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        event.currentTarget?.getAttribute("aria-expanded") !== "true"
      ) {
        event.stopPropagation();
      }
      onKeyDown?.(event);
    },
    [singleLine, onKeyDown]
  );

  return (
    <MentionsInput
      /*
        Los gestores de contraseñas ofrecen autocompletar acá: ven una caja de
        texto sin `autocomplete` dentro de algo que parece un formulario y
        asumen credenciales. Un comentario nunca lo es, así que se les dice que
        la ignoren. Cada uno mira su propio atributo —1Password, LastPass,
        Bitwarden, Dashlane— y `autoComplete="off"` solo no alcanza: varios lo
        ignoran a propósito, porque las páginas lo usaban para molestar.
      */
      autoComplete="off"
      data-1p-ignore="true"
      data-lpignore="true"
      data-bwignore="true"
      data-form-type="other"
      className={className}
      style={style ?? getMentionsInputStyles()}
      suggestionsPortalHost={MENTIONS_SUGGESTIONS_PORTAL_HOST}
      allowSuggestionsAboveCursor={allowSuggestionsAboveCursor}
      singleLine={singleLine}
      inputRef={assignInputRef}
      rows={singleLine ? undefined : rows}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
}
